// Fristen im Kalender: Rechnungs-Fälligkeiten + Skonto, Ticket-Fristen und
// KI-erkannte E-Mail-Fristen als VIRTUELLE Kalender-Einträge — bewusst KEINE
// termine-Zeilen (nichts davon läuft in den Outlook-Sync oder blockiert
// Buchungslink-Slots). Quellen werden live gerechnet, keine eigene Tabelle.
import { supabase } from '../supabase/client';

export interface FristRohdaten {
  rechnungen: { id: string; rechnungsnummer: string | null; unternehmen: string | null; brutto: number | null; faelligkeitsdatum: string | null; skonto_bis: string | null; skonto_prozent: number | null }[];
  tickets: { id: string; ticket_nummer: string | null; titel: string; faellig_am: string | null }[];
  emailFristen: { frist_datum: string; email: { id: string; betreff: string | null } | null }[];
  wiedervorlagen: { wiedervorlage_am: string; id: string; betreff: string | null }[];
}

export interface FristEintrag {
  id: string;                 // 'frist-rechnung-…' | 'frist-skonto-…' | 'frist-ticket-…' | 'frist-email-…'
  datum: string;              // 'YYYY-MM-DD'
  titel: string;
  untertitel: string | null;
  ueberfaellig: boolean;
  ziel: { typ: 'rechnung' | 'ticket' | 'email'; id: string };
}

const datumVon = (wert: string): string => wert.slice(0, 10);

const betragText = (brutto: number | null): string | null =>
  brutto == null ? null : `${brutto.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/** Pure: Rohdaten → sortierte Frist-Einträge; `heute` = 'YYYY-MM-DD' (Berlin-Kalendertag). */
export function baueFristEintraege(roh: FristRohdaten, heute: string): FristEintrag[] {
  const eintraege: FristEintrag[] = [];

  for (const r of roh.rechnungen) {
    const wer = [r.unternehmen, r.rechnungsnummer].filter(Boolean).join(' · ') || 'Rechnung';
    if (r.faelligkeitsdatum) {
      const datum = datumVon(r.faelligkeitsdatum);
      eintraege.push({
        id: `frist-rechnung-${r.id}`,
        datum,
        titel: `Rechnung fällig: ${wer}`,
        untertitel: betragText(r.brutto),
        ueberfaellig: datum < heute,
        ziel: { typ: 'rechnung', id: r.id },
      });
    }
    if (r.skonto_bis) {
      const datum = datumVon(r.skonto_bis);
      const prozent = r.skonto_prozent ? ` (${String(r.skonto_prozent).replace('.', ',')} %)` : '';
      eintraege.push({
        id: `frist-skonto-${r.id}`,
        datum,
        titel: `Skonto bis: ${wer}${prozent}`,
        untertitel: betragText(r.brutto),
        ueberfaellig: datum < heute,
        ziel: { typ: 'rechnung', id: r.id },
      });
    }
  }

  for (const t of roh.tickets) {
    if (!t.faellig_am) continue;
    const datum = datumVon(t.faellig_am);
    eintraege.push({
      id: `frist-ticket-${t.id}`,
      datum,
      titel: `Ticket-Frist: ${t.titel}`,
      untertitel: t.ticket_nummer || null,
      ueberfaellig: datum < heute,
      ziel: { typ: 'ticket', id: t.id },
    });
  }

  for (const e of roh.emailFristen) {
    if (!e.email) continue;
    const datum = datumVon(e.frist_datum);
    eintraege.push({
      id: `frist-email-${e.email.id}`,
      datum,
      titel: `Frist aus E-Mail: ${e.email.betreff || '(ohne Betreff)'}`,
      untertitel: null,
      ueberfaellig: datum < heute,
      ziel: { typ: 'email', id: e.email.id },
    });
  }

  for (const w of roh.wiedervorlagen) {
    const datum = datumVon(w.wiedervorlage_am);
    eintraege.push({
      id: `frist-wiedervorlage-${w.id}`,
      datum,
      titel: `Wiedervorlage: ${w.betreff || '(ohne Betreff)'}`,
      untertitel: null,
      ueberfaellig: datum < heute,
      ziel: { typ: 'email', id: w.id },
    });
  }

  return eintraege.sort((a, b) => a.datum.localeCompare(b.datum) || a.titel.localeCompare(b.titel));
}

/** Lädt alle offenen Fristen eines Monats (rangeStart inkl., rangeEnd exkl., 'YYYY-MM-DD'). */
export async function ladeFristen(rangeStart: string, rangeEnd: string): Promise<FristRohdaten> {
  const [rechnungenQ, skontoQ, ticketsQ, emailsQ, wiedervorlagenQ] = await Promise.all([
    supabase.from('rechnungen')
      .select('id, rechnungsnummer, unternehmen, brutto, faelligkeitsdatum, skonto_bis, skonto_prozent')
      .neq('status', 'bezahlt')
      .gte('faelligkeitsdatum', rangeStart).lt('faelligkeitsdatum', rangeEnd),
    // Skonto-Fristen, deren Fälligkeit außerhalb des Monats liegt, sonst fehlen sie
    supabase.from('rechnungen')
      .select('id, rechnungsnummer, unternehmen, brutto, faelligkeitsdatum, skonto_bis, skonto_prozent')
      .neq('status', 'bezahlt')
      .gte('skonto_bis', rangeStart).lt('skonto_bis', rangeEnd),
    supabase.from('tickets')
      .select('id, ticket_nummer, titel, faellig_am')
      .not('status', 'in', '(erledigt,geschlossen)')
      .gte('faellig_am', rangeStart).lt('faellig_am', rangeEnd),
    supabase.from('email_analysen')
      .select('frist_datum, email:emails!inner(id, betreff, erledigt, archiviert_am)')
      .gte('frist_datum', rangeStart).lt('frist_datum', rangeEnd)
      .eq('email.erledigt', false).is('email.archiviert_am', null),
    supabase.from('emails')
      .select('id, betreff, wiedervorlage_am')
      .gte('wiedervorlage_am', rangeStart).lt('wiedervorlage_am', rangeEnd).is('geloescht_am', null),
  ]);

  // Rechnungen aus beiden Queries zusammenführen (eine Rechnung kann in beiden stehen) —
  // baueFristEintraege erzeugt Fälligkeits-/Skonto-Einträge nur bei Datum im Fenster
  // ohnehin doppelt-sicher über die eindeutigen Eintrags-IDs.
  const proId = new Map<string, FristRohdaten['rechnungen'][number]>();
  for (const r of [...(rechnungenQ.data || []), ...(skontoQ.data || [])]) proId.set(r.id, r);
  // Fälligkeit/Skonto außerhalb des Fensters nullen, damit keine Einträge in fremden
  // Monaten entstehen (die Merge-Zeile kennt sonst beide Daten).
  const rechnungen = Array.from(proId.values()).map((r) => ({
    ...r,
    faelligkeitsdatum: r.faelligkeitsdatum && r.faelligkeitsdatum.slice(0, 10) >= rangeStart && r.faelligkeitsdatum.slice(0, 10) < rangeEnd ? r.faelligkeitsdatum : null,
    skonto_bis: r.skonto_bis && r.skonto_bis.slice(0, 10) >= rangeStart && r.skonto_bis.slice(0, 10) < rangeEnd ? r.skonto_bis : null,
  }));

  return {
    rechnungen,
    tickets: (ticketsQ.data as FristRohdaten['tickets']) || [],
    emailFristen: ((emailsQ.data as unknown as FristRohdaten['emailFristen']) || []),
    wiedervorlagen: (wiedervorlagenQ.data as unknown as FristRohdaten['wiedervorlagen']) || [],
  };
}
